import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MdLock, MdArrowBack, MdCheckCircle } from 'react-icons/md';
import './GuestOtpVerification.css';

const OTP_LENGTH = 6;

const GuestOtpVerification: React.FC = () => {
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const mobile = location.state?.mobile || '';

  useEffect(() => {
    // Auto-focus first input
    inputsRef.current[0]?.focus();
  }, []);

  // Clear stored session on mount to force re-authentication
  useEffect(() => {
    localStorage.removeItem('guestSession');
  }, []);

  useEffect(() => {
    if (shake) {
      const timer = setTimeout(() => setShake(false), 500);
      return () => clearTimeout(timer);
    }
  }, [shake]);

  const handleOtpChange = (idx: number, value: string) => {
    if (!/^[0-9]?$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[idx] = value;
    setOtp(newOtp);
    
    // Auto-focus next input
    if (value && idx < OTP_LENGTH - 1) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (otp[idx] === '' && idx > 0) {
        inputsRef.current[idx - 1]?.focus();
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    const otpValue = otp.join('');
    if (otpValue.length !== OTP_LENGTH) {
      setError('Please enter the complete 6-digit OTP');
      setShake(true);
      setIsLoading(false);
      return;
    }

    setLoading(true);
    
    // Simulate API call delay
    setTimeout(() => {
      setLoading(false);
      setIsLoading(false);
      
      // Check if OTP is 123456 (demo OTP)
      if (otpValue === '123456') {
        setSuccess(true);
        setTimeout(() => {
          // Navigate to the main guest app with static hotel/room IDs
          navigate('/eyJob3RlbF9pZCI6MTAwMCwicm9vbV9pZCI6MX0=/explore', { replace: true });
        }, 1000);
      } else {
        setError('Invalid OTP. Please try again.');
        setShake(true);
        setOtp(Array(OTP_LENGTH).fill(''));
        inputsRef.current[0]?.focus();
      }
    }, 1500);
  };

  const handleBack = () => {
    navigate('/');
  };

  return (
    <div className="guest-otp-container">
      <div className="otp-background">
        <div className="gradient-overlay"></div>
        <div className="floating-elements">
          <div className="floating-circle circle-1"></div>
          <div className="floating-circle circle-2"></div>
          <div className="floating-circle circle-3"></div>
        </div>
      </div>
      
      <div className="otp-card">
        <div className="otp-header">
          <button className="back-btn" onClick={handleBack}>
            <MdArrowBack />
          </button>
          <div className="logo-container">
            <div className="logo-icon">
              <MdLock />
            </div>
          </div>
          <h1 className="otp-title">Verify Your Identity</h1>
          <p className="otp-subtitle">
            We've sent a verification code to <strong>{mobile || 'your mobile number'}</strong>
          </p>
        </div>

        <div className="otp-content">
          <form onSubmit={handleSubmit} className={`otp-form ${shake ? 'shake' : ''}`}>
            <div className="otp-container">
              <label className="form-label">
                <i className="fas fa-shield-alt"></i>
                Enter 6-digit Verification Code
              </label>
              <div className="otp-inputs">
                {otp.map((digit, idx) => (
                  <input
                    key={idx}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    className="otp-input"
                    value={digit}
                    ref={el => { inputsRef.current[idx] = el; }}
                    onChange={e => handleOtpChange(idx, e.target.value)}
                    onKeyDown={e => handleOtpKeyDown(idx, e)}
                    autoFocus={idx === 0}
                    disabled={isLoading}
                  />
                ))}
              </div>
              <p className="otp-hint">
                Didn't receive the code? 
                <button 
                  type="button" 
                  className="resend-link"
                  onClick={() => navigate('/')}
                  disabled={isLoading}
                >
                  Resend
                </button>
              </p>
            </div>

            {error && (
              <div className="error-message">
                <i className="fas fa-exclamation-circle"></i>
                {error}
              </div>
            )}

            <button 
              type="submit" 
              className={`submit-btn ${isLoading ? 'loading' : ''}`}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="spinner"></div>
                  <span>Verifying...</span>
                </>
              ) : success ? (
                <>
                  <MdCheckCircle />
                  <span>Success!</span>
                </>
              ) : (
                <>
                  <i className="fas fa-unlock"></i>
                  <span>Verify & Continue</span>
                </>
              )}
            </button>
          </form>
        </div>

        <div className="otp-footer">
          <p className="footer-text">
            Secure verification powered by <span className="brand">QRoom</span>
          </p>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          .guest-otp-container {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            position: relative;
            overflow: hidden;
            padding: 20px;
          }

          .otp-background {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 1;
          }

          .gradient-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(135deg, rgba(102, 126, 234, 0.9) 0%, rgba(118, 75, 162, 0.9) 100%);
          }

          .floating-elements {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
          }

          .floating-circle {
            position: absolute;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.1);
            animation: float 6s ease-in-out infinite;
          }

          .circle-1 {
            width: 100px;
            height: 100px;
            top: 20%;
            left: 10%;
            animation-delay: 0s;
          }

          .circle-2 {
            width: 150px;
            height: 150px;
            top: 60%;
            right: 15%;
            animation-delay: 2s;
          }

          .circle-3 {
            width: 80px;
            height: 80px;
            bottom: 20%;
            left: 20%;
            animation-delay: 4s;
          }

          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-20px) rotate(180deg); }
          }

          .otp-card {
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(20px);
            border-radius: 24px;
            padding: 40px;
            width: 100%;
            max-width: 420px;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
            position: relative;
            z-index: 2;
            border: 1px solid rgba(255, 255, 255, 0.2);
          }

          .back-btn {
            position: absolute;
            top: 20px;
            left: 20px;
            background: rgba(255, 255, 255, 0.2);
            border: none;
            border-radius: 50%;
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            cursor: pointer;
            transition: all 0.3s ease;
            backdrop-filter: blur(10px);
          }

          .back-btn:hover {
            background: rgba(255, 255, 255, 0.3);
            transform: scale(1.1);
          }

          .otp-header {
            text-align: center;
            margin-bottom: 32px;
          }

          .logo-container {
            margin-bottom: 20px;
          }

          .logo-icon {
            width: 80px;
            height: 80px;
            background: linear-gradient(135deg, #4a6cf7 0%, #2254e0 100%);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto;
            box-shadow: 0 8px 32px rgba(74, 108, 247, 0.3);
          }

          .logo-icon svg {
            font-size: 32px;
            color: white;
          }

          .otp-title {
            font-size: 28px;
            font-weight: 700;
            color: #1a1a1a;
            margin-bottom: 8px;
          }

          .otp-subtitle {
            font-size: 16px;
            color: #666;
            line-height: 1.5;
          }

          .otp-container {
            margin-bottom: 24px;
          }

          .form-label {
            display: flex;
            align-items: center;
            gap: 8px;
            font-weight: 600;
            color: #333;
            margin-bottom: 12px;
            font-size: 14px;
            justify-content: center;
          }

          .form-label i {
            color: #4a6cf7;
          }

          .otp-inputs {
            display: flex;
            gap: 12px;
            justify-content: center;
            margin-top: 16px;
          }

          .otp-input {
            width: 50px;
            height: 50px;
            border: 2px solid #e1e5e9;
            border-radius: 12px;
            text-align: center;
            font-size: 20px;
            font-weight: 600;
            transition: all 0.3s ease;
            background: white;
          }

          .otp-input:focus {
            outline: none;
            border-color: #4a6cf7;
            box-shadow: 0 0 0 4px rgba(74, 108, 247, 0.1);
          }

          .otp-hint {
            text-align: center;
            margin-top: 16px;
            font-size: 14px;
            color: #666;
          }

          .resend-link {
            background: none;
            border: none;
            color: #4a6cf7;
            font-weight: 600;
            cursor: pointer;
            margin-left: 4px;
          }

          .resend-link:hover {
            text-decoration: underline;
          }

          .error-message {
            background: #fee;
            color: #d32f2f;
            padding: 12px 16px;
            border-radius: 8px;
            margin-bottom: 16px;
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 14px;
            border: 1px solid #ffcdd2;
          }

          .submit-btn {
            width: 100%;
            padding: 16px;
            background: linear-gradient(135deg, #4a6cf7 0%, #2254e0 100%);
            color: white;
            border: none;
            border-radius: 12px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            position: relative;
          }

          .submit-btn:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(74, 108, 247, 0.3);
          }

          .submit-btn:disabled {
            opacity: 0.7;
            cursor: not-allowed;
          }

          .submit-btn.loading {
            pointer-events: none;
          }

          .spinner {
            width: 20px;
            height: 20px;
            border: 2px solid rgba(255, 255, 255, 0.3);
            border-top: 2px solid white;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          .otp-footer {
            text-align: center;
            margin-top: 32px;
            padding-top: 24px;
            border-top: 1px solid #e1e5e9;
          }

          .footer-text {
            font-size: 14px;
            color: #999;
          }

          .brand {
            color: #4a6cf7;
            font-weight: 600;
          }

          .shake {
            animation: shake 0.5s ease-in-out;
          }

          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-8px); }
            75% { transform: translateX(8px); }
          }

          @media (max-width: 480px) {
            .otp-card {
              padding: 24px;
              margin: 16px;
            }
            
            .otp-inputs {
              gap: 8px;
            }
            
            .otp-input {
              width: 45px;
              height: 45px;
              font-size: 18px;
            }
          }
        `
      }} />
    </div>
  );
};

export default GuestOtpVerification; 